import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PaperAirplaneIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { fetchJournal, fetchJournalPdf, sendJournal } from "../../../api/journaling";
import JournalEntries from "./JournalEntries";
import DocumentationSendDialog from "../shared/DocumentationSendDialog";
import SendLogPanel from "../shared/SendLogPanel";

export default function JournalDetailDialog({ open, onClose, journal, onUpdated }) {
  const { t } = useTranslation();
  const [isPrinting, setIsPrinting] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [detail, setDetail] = useState(journal);
  const journalIdRef = useRef(journal?.id ?? null);

  useEffect(() => {
    const id = detail?.id ?? journal?.id;
    if (id) journalIdRef.current = id;
  }, [detail?.id, journal?.id]);

  useEffect(() => {
    if (!open) {
      setSendOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !journal?.id) {
      setDetail(journal);
      return;
    }
    fetchJournal(journal.id).then((data) => {
      if (data?.id) setDetail(data);
    });
  }, [open, journal?.id, journal]);

  const handlePrint = async () => {
    if (!detail?.id) return;
    setIsPrinting(true);
    try {
      const res = await fetchJournalPdf(detail.id);
      if (res?.blob) {
        const url = window.URL.createObjectURL(res.blob);
        window.open(url, "_blank", "noopener");
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }
      toast.error(t("journal.print_failed"));
    } catch {
      toast.error(t("journal.print_failed"));
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSent = (response) => {
    if (!response) return;
    const journalId = journalIdRef.current ?? detail?.id ?? journal?.id;
    const merged = { ...response, id: response.id ?? journalId };
    setDetail((prev) => (prev ? { ...prev, ...merged } : merged));
    onUpdated?.(merged);
  };

  return (
    <>
      <PMDialog
        open={open && !sendOpen}
        onClose={onClose}
        title={detail?.title || ""}
        size="3xl"
        closeOnOutsideClick
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {detail?.description ? (
              <p className="text-sm text-slate-600">{detail.description}</p>
            ) : null}
            {detail?.last_sent_at ? (
              <p className="mt-2 text-sm font-medium text-emerald-700">
                {t("documentationSend.sent_badge", {
                  date: new Date(detail.last_sent_at).toLocaleString(),
                })}
              </p>
            ) : null}
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSendOpen(true)}
              disabled={!detail?.id}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {t("documentationSend.send_journal")}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={isPrinting || !detail?.id}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              <PrinterIcon className="h-4 w-4" />
              {isPrinting ? t("journal.printing") : t("journal.print")}
            </button>
          </div>
        </div>

        {detail?.id ? <JournalEntries journalId={detail.id} /> : null}

        {detail?.id ? (
          <div className="mt-6 border-t border-slate-100 pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              {t("documentationSend.history_title")}
            </h3>
            <SendLogPanel sendLog={detail.send_log} lastSentAt={detail.last_sent_at} />
          </div>
        ) : null}
      </PMDialog>

      <DocumentationSendDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        resourceType="journal"
        resourceTitle={detail?.title}
        suggestedRecipients={detail?.suggested_recipients}
        allowMultiple
        onSend={(values) => {
          const journalId = journalIdRef.current ?? detail?.id ?? journal?.id;
          if (!journalId) {
            return Promise.reject(new Error(t("documentationSend.failed")));
          }
          return sendJournal(journalId, {
            email: values.email,
            name: values.name,
            message: values.message,
            recipients: values.recipients,
          });
        }}
        onSent={handleSent}
      />
    </>
  );
}
